// src/contexts/UIContext.tsx
import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface UIState {
  isRecording: boolean;
  isCapturingPhoto: boolean;
  isLocationLoading: boolean;
  showLocationPermission: boolean;
  showCameraPermission: boolean;
  isAudioPlaying: boolean;
  currentAudioUrl: string | null;
  showInstallPrompt: boolean;
}

type UIAction =
  | { type: 'SET_RECORDING'; payload: boolean }
  | { type: 'SET_CAPTURING_PHOTO'; payload: boolean }
  | { type: 'SET_LOCATION_LOADING'; payload: boolean }
  | { type: 'SET_SHOW_LOCATION_PERMISSION'; payload: boolean }
  | { type: 'SET_SHOW_CAMERA_PERMISSION'; payload: boolean }
  | { type: 'SET_AUDIO_PLAYING'; payload: boolean }
  | { type: 'SET_CURRENT_AUDIO_URL'; payload: string | null }
  | { type: 'SET_SHOW_INSTALL_PROMPT'; payload: boolean };

const initialState: UIState = {
  isRecording: false,
  isCapturingPhoto: false,
  isLocationLoading: false,
  showLocationPermission: false,
  showCameraPermission: false,
  isAudioPlaying: false,
  currentAudioUrl: null,
  showInstallPrompt: false,
};

const uiReducer = (state: UIState, action: UIAction): UIState => {
  switch (action.type) {
    case 'SET_RECORDING':
      return { ...state, isRecording: action.payload };
    case 'SET_CAPTURING_PHOTO':
      return { ...state, isCapturingPhoto: action.payload };
    case 'SET_LOCATION_LOADING':
      return { ...state, isLocationLoading: action.payload };
    case 'SET_SHOW_LOCATION_PERMISSION':
      return { ...state, showLocationPermission: action.payload };
    case 'SET_SHOW_CAMERA_PERMISSION':
      return { ...state, showCameraPermission: action.payload };
    case 'SET_AUDIO_PLAYING':
      return { ...state, isAudioPlaying: action.payload };
    case 'SET_CURRENT_AUDIO_URL':
      return { ...state, currentAudioUrl: action.payload };
    case 'SET_SHOW_INSTALL_PROMPT':
      return { ...state, showInstallPrompt: action.payload };
    default:
      return state;
  }
};

interface UIContextType {
  state: UIState;
  dispatch: React.Dispatch<UIAction>;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  return (
    <UIContext.Provider value={{ state, dispatch }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};